import itemService from '../../../services/itemService';
import filterService from '../../../services/filterService';

import {
    setItemLoading,
    setItemError,
    setItemList,
    setItemCurrent,
    setItemPagination,
    setItemSort,
    setItemFilters,
    setItemDefaultFilter,
    setItemSettings,
    clearItemCurrent,
    clearItemError,
    setItemFilterLoading,
    changeItemFilterCondition,
    addItemFilterCondition,
    updateItemFilterConditionRule,
    updateItemFilterConditionValue,
    removeItemFilterCondition,
    setItemSelected,
    resetItemFiltersAndPagination
} from './itemSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().item.isSettingsInitialized) {
            dispatch(setItemLoading(true));
            const response = await itemService.getSettings();

            // Set the settings
            dispatch(setItemSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setItemDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setItemError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setItemLoading(false));
    }
};

export const fetchItems = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().item;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setItemLoading(true));
        const response = await itemService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setItemList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchItems:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemLoading(false));
    }
};

export const fetchFinishedItems = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().item;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const condition = {
            rules: {
                condition: 'and',
                children: [
                    {
                        type: 'rule',
                        query: {
                            operator: 'equal',
                            rule: 'items.item_type',
                            type: 'text',
                            value: 'finished_goods'
                        }
                    }
                ]
            }
        }

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : condition;

        dispatch(setItemLoading(true));
        const response = await itemService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setItemList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchItems:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemLoading(false));
    }
};


export const fetchRawMaterialItems = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().item;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const condition = {
            rules: {
                condition: 'and',
                children: [
                    {
                        type: 'rule',
                        query: {
                            operator: 'equal',
                            rule: 'items.item_type',
                            type: 'text',
                            value: 'raw_material'
                        }
                    }
                ]
            }
        }

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : condition;

        dispatch(setItemLoading(true));
        const response = await itemService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setItemList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchItems:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemLoading(false));
    }
};



export const fetchItem = (id) => async (dispatch) => {
    try {
        dispatch(setItemLoading(true));
        const response = await itemService.getOne(id);
        dispatch(setItemCurrent(response.data));
    } catch (error) {
        dispatch(setItemError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setItemLoading(false));
    }
};

export const createItem = (data) => async (dispatch) => {
    try {
        const response = await itemService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateItem = (id, data) => async (dispatch) => {
    try {
        const response = await itemService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteItem = (id) => async (dispatch) => {
    try {
        dispatch(setItemLoading(true));
        const response = await itemService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setItemPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setItemSort(sort));
};

export const searchItems = (query) => (dispatch, getState) => {
    const currentFilters = getState().item.filters;
    dispatch(setItemFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setItemDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeItemFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addItemFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateItemFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateItemFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeItemFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setItemFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().item.settings;
            dispatch(setItemSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setItemFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setItemFilterLoading(false));
        dispatch(setItemError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setItemSelected(selectedRows));
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetItemFiltersAndPagination());
};